import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { getProductData, updateProduct } from '../../services/Product/productService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import InputField from '../Utils/InputField';
import MeasureUnits from '../Constants/measureUnits';
import { usePermissions } from '../../hooks/usePermissions';

const EditProductModal = ({ show, handleClose, product, refreshProducts }) => {
  const { register, handleSubmit, setValue, formState: { errors }, reset, control } = useForm();
  const { hasPermission } = usePermissions();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const canDelete = hasPermission('productDelete');

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        if (product?.id) {
          const data = await getProductData(product.id);
          setValue('oldId', data?.oldId);
          setValue('description', data.description);
          setValue('unit', data.unit);
          setValue('unitWeight', data.unitWeight);
          setValue('stock', data.stock);
          setValue('active', data?.active);
        }
      } catch (error) {
        setErrorMessage(error.message || 'Erro ao buscar dados do produto.');
        setShowErrorModal(true);
      }
    };

    if (show && product) {
      fetchProductData();
    } else if (!show) {
      reset();
    }
  }, [show, product, setValue, reset]);

  const onSubmit = async (data) => {
    const payload = {
      ...data
    };

    try {
      await updateProduct(product.id, payload);
      setSuccessMessage('Produto atualizado com sucesso!');
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao atualizar produto.');
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshProducts();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Editar Produto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={12}>
                <InputField
                  label="Descrição"
                  name="description"
                  placeholder="Digite a descrição do produto"
                  register={register}
                  errors={errors}
                  rules={{ required: 'Descrição é obrigatória' }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="unit"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Unidade é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <Form.Group controlId="unit" className="mb-3">
                      <Form.Label className='mb-1'>Unidade</Form.Label>
                      <Form.Control
                        as="select"
                        {...field}
                        isInvalid={!!fieldState.error}
                      >
                        <option value="">Selecione...</option>
                        {Object.entries(MeasureUnits).map(([key, { value, label }]) => (
                          <option key={key} value={value}>{label}</option>
                        ))}
                      </Form.Control>
                      {fieldState.error && (
                        <Form.Control.Feedback type="invalid">
                          {fieldState.error.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  )}
                />
              </Col>
              <Col md={6}>
                <InputField
                  label="Peso Unidade (kg)"
                  name="unitWeight"
                  placeholder="Digite o peso"
                  type="number"
                  step="0.01"
                  register={register}
                  errors={errors}
                  rules={{ required: 'Peso é obrigatório', min: { value: 0.01, message: 'Peso deve ser maior que zero' } }}
                />
              </Col>
            </Row>
            <Row className='mt-2'>
              <Col md={12}>
                <Form.Label className="center">Status</Form.Label>
                <Controller
                  name="active"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Form.Check
                      type="checkbox"
                      label="Ativo"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canDelete}
                    />
                  )}
                />
              </Col>   
            </Row>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Atualizar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditProductModal;